import {
  ShareIcon,
  ShoppingCartIcon,
  SpeakerWaveIcon,
} from "@heroicons/react/24/outline";
import { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { BookContext } from "../contexts/BookContext";
import { AudioBookDisabledIcon, AudioBookIcon } from "../icons";
import { DownloadAudioBookButton } from "./Buttons/DownloadAudioBookButton";
import { ValidateAudioBookModal } from "./Modals/ValidateAudioBookModal";
import { VoiceClonerModal } from "./Modals/VoiceClonerModal";

export const AudioBookCard: FC = () => {
  const { t } = useTranslation();
  const { book, subscription } = useContext(BookContext);
  const isAudioBookSubscribed = subscription?.with_audiobook;
  const isAudioGenerating = book?.status === "audio_generating";
  const isAudioGenerated = book?.status === "audio_generated";
  const isAudioValidated = book?.status === "audio_validated";
  const [open, setOpen] = useState(false);
  const [validateModalOpen, setValidateModalOpen] = useState(false);
  const upsellUrl = import.meta.env.VITE_MEMOIRS_UPSELL_AUDIO_URL;
  const audioBookPublicUrl = `${
    import.meta.env.VITE_MEMOIRS_APP_URL
  }/audio_book?token=${book?.audio_book_token}`;

  const title = () => {
    if (isAudioBookSubscribed) {
      if (isAudioGenerating) {
        return t("dashboard.audio_book_generating");
      } else if (isAudioGenerated || isAudioValidated) {
        return t("dashboard.audio_book_generated");
      } else {
        return t("dashboard.audio_book_activated");
      }
    } else {
      return t("dashboard.audio_book_available");
    }
  };

  const description = () => {
    if (isAudioBookSubscribed) {
      if (isAudioGenerating) {
        return t("dashboard.audioBookGeneratingDescription");
      } else if (isAudioGenerated) {
        return t("dashboard.audioBookGeneratedDescription");
      } else if (isAudioValidated) {
        return t("dashboard.audioBookValidatedDescription");
      } else {
        return t("dashboard.audioBookDescription");
      }
    } else {
      return t("dashboard.audioBookDescription");
    }
  };

  const icon = () => {
    if (isAudioBookSubscribed) {
      return (
        <AudioBookIcon className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24" />
      );
    } else {
      return (
        <AudioBookDisabledIcon className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24" />
      );
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(audioBookPublicUrl);
      toast.success(t("dashboard.link_copied"));
    } catch (err) {
      console.error("Erreur lors de la copie:", err);
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement("textarea");
      textArea.value = audioBookPublicUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const links = () => {
    if (!book) return <></>;
    if (isAudioBookSubscribed) {
      if (isAudioGenerating) {
        return <></>;
      } else if (isAudioGenerated) {
        return (
          <button
            onClick={() => setValidateModalOpen(true)}
            className="flex w-full items-center justify-center bg-delicate-800 hover:bg-delicate-500 text-white text-sm sm:text-lg rounded px-4 sm:px-6 py-2"
          >
            <SpeakerWaveIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            {t("dashboard.validateAudioBook")}
          </button>
        );
      } else if (isAudioValidated) {
        return (
          <>
            <DownloadAudioBookButton />
            <button
              onClick={handleCopyToClipboard}
              className="flex w-full items-center justify-center bg-white hover:bg-rust-800 text-rust-500 hover:text-white border border-rust-500 text-sm sm:text-lg rounded px-4 sm:px-6 py-2"
            >
              <ShareIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              {t("dashboard.share_audiobook")}
            </button>
          </>
        );
      } else {
        return (
          <button
            onClick={() => setOpen(true)}
            className="flex w-full items-center justify-center bg-delicate-800 hover:bg-delicate-500 text-white text-sm sm:text-lg rounded px-4 sm:px-6 py-2"
          >
            <SpeakerWaveIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            {t("dashboard.generate_audio_book")}
          </button>
        );
      }
    } else {
      return (
        <Link
          to={upsellUrl}
          target="blank"
          className="flex w-full sm:w-80 items-center justify-center bg-rust-500 hover:bg-rust-800 text-white text-sm sm:text-lg rounded px-4 sm:px-6 py-2"
          download
        >
          <ShoppingCartIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
          {t("dashboard.buy_now")}
        </Link>
      );
    }
  };

  return (
    <>
      <div className="bg-white w-full lg:w-1/2 flex flex-col justify-between shadow-md rounded py-4 sm:py-6 px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row w-full justify-between items-start sm:items-center">
          <div className="flex flex-col w-full sm:w-4/6 mb-4 sm:mb-0">
            <p className="text-slate-700 text-2xl sm:text-3xl lg:text-4xl font-bold">
              {title()}
            </p>
            <p className="text-slate-50 text-sm sm:text-md mt-2 sm:mt-4">
              {description()}
            </p>
          </div>
          <div className="flex flex-col w-full sm:w-1/6 items-center justify-center rounded border border-gray-300 py-4">
            {icon()}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row w-full justify-between items-center mt-4 sm:mt-6 space-y-4 sm:space-y-0 sm:space-x-4">
          {links()}
        </div>
      </div>
      {open && <VoiceClonerModal open={open} setOpen={setOpen} />}
      {validateModalOpen && (
        <ValidateAudioBookModal
          open={validateModalOpen}
          setOpen={setValidateModalOpen}
        />
      )}
    </>
  );
};
